import React, { useState } from 'react';
import { COMPANY_INFO } from '../data/siteData';

export default function ContactForm({
  title = 'Indhent et uforpligtende tilbud',
  subtitle = 'Udfyld formularen herunder og vi vender tilbage inden for 24 timer.',
  defaultSubject = '',
  className = '',
  variant = 'card', // 'card' | 'minimal-dark'
}) {
  const [formData, setFormData] = useState({
    navn: '',
    postnummer: '',
    telefon: '',
    email: '',
    besked: defaultSubject ? `Vedrørende: ${defaultSubject}\n` : '',
    website_url: '', // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = 'Angiv venligst dit fulde navn';
    if (!formData.telefon.trim()) errs.telefon = 'Angiv venligst et telefonnummer';
    if (!formData.email.trim()) {
      errs.email = 'Angiv venligst din e-mailadresse';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Angiv venligst en gyldig e-mailadresse';
    }
    if (!formData.besked.trim()) errs.besked = 'Beskriv venligst din opgave';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.navn,
          postnummer: formData.postnummer,
          telefon: formData.telefon,
          email: formData.email,
          besked: formData.besked,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitError(
          errorData.message ||
            'Der opstod en fejl ved afsendelse af formularen. Ring venligst direkte på 27 13 57 87.'
        );
      }
    } catch {
      setSubmitError(
        'Forbindelsesfejl. Prøv venligst igen eller ring direkte til os på 27 13 57 87.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    if (variant === 'minimal-dark') {
      return (
        <div className={`bg-white/10 backdrop-blur-md rounded-2xl p-8 sm:p-10 border border-white/20 text-center text-white ${className}`}>
          <div className="w-16 h-16 bg-[#52b788] text-[#0b1f15] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">Tak for din henvendelse!</h3>
          <p className="text-white/80 mb-6 max-w-md mx-auto">
            Vi har modtaget din besked og kontakter dig hurtigst muligt – typisk inden for 24 timer på hverdage.
          </p>
          <div className="p-4 bg-white/10 rounded-xl text-sm text-white/90 max-w-md mx-auto mb-6 border border-white/15">
            <p className="font-semibold mb-1 text-[#52b788]">Har du en hasteopgave?</p>
            <p>
              Du er altid velkommen til at ringe direkte til os på{' '}
              <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="font-bold underline whitespace-nowrap text-[#52b788]">
                {COMPANY_INFO.phone}
              </a>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                navn: '',
                postnummer: '',
                telefon: '',
                email: '',
                besked: '',
                website_url: '',
              });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/60 text-white hover:bg-white/10 font-medium text-sm transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-rotate-left" aria-hidden="true" />
            <span>Send en ny besked</span>
          </button>
        </div>
      );
    }

    return (
      <div className={`bg-white rounded-2xl p-8 sm:p-10 border border-[#d2dfd8] shadow-sm text-center ${className}`}>
        <div className="w-16 h-16 bg-[#e4eee8] text-[#1b4332] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-[#143627] mb-3">Tak for din henvendelse!</h3>
        <p className="text-[#3c4a41] mb-6 max-w-md mx-auto">
          Vi har modtaget din besked og kontakter dig hurtigst muligt – typisk inden for 24 timer på hverdage.
        </p>
        <div className="p-4 bg-[#f2f7f4] rounded-xl text-sm text-[#274737] max-w-md mx-auto mb-6">
          <p className="font-semibold mb-1">Har du en hasteopgave?</p>
          <p>
            Du er altid velkommen til at ringe direkte til os på{' '}
            <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="font-bold underline whitespace-nowrap">
              {COMPANY_INFO.phone}
            </a>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setFormData({
              navn: '',
              postnummer: '',
              telefon: '',
              email: '',
              besked: '',
              website_url: '',
            });
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#357959] text-[#1f4a36] hover:bg-[#f2f7f4] font-medium text-sm transition-colors"
        >
          <i className="fa-solid fa-arrow-rotate-left" aria-hidden="true" />
          <span>Send en ny besked</span>
        </button>
      </div>
    );
  }

  if (variant === 'minimal-dark') {
    return (
      <div className={`w-full ${className}`}>
        {submitError && (
          <div className="mb-6 p-4 rounded-xl bg-red-950/80 border border-red-500/50 text-red-200 text-sm flex items-start gap-3">
            <i className="fa-solid fa-circle-exclamation text-red-400 mt-0.5" aria-hidden="true" />
            <p>{submitError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Honeypot field for antispam - hidden from humans */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url_dark">Website URL (leave blank)</label>
            <input
              id="website_url_dark"
              name="website_url"
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {/* Top row: 50/50 split for Name and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="form-navn-dark" className="block text-xs font-semibold text-white/80 mb-1">
                Dit navn <span className="text-[#52b788]">*</span>
              </label>
              <input
                id="form-navn-dark"
                type="text"
                name="navn"
                required
                autoComplete="name"
                placeholder="F.eks. Anders Jensen"
                value={formData.navn}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b pb-3 pt-2 text-white placeholder:text-white/40 text-base focus:outline-none focus:ring-0 rounded-none transition-colors ${
                  errors.navn
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-white/30 focus:border-[#52b788]'
                }`}
              />
              {errors.navn && <p className="mt-1.5 text-xs text-red-400">{errors.navn}</p>}
            </div>

            <div>
              <label htmlFor="form-email-dark" className="block text-xs font-semibold text-white/80 mb-1">
                E-mailadresse <span className="text-[#52b788]">*</span>
              </label>
              <input
                id="form-email-dark"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="F.eks. navn@mail.dk"
                value={formData.email}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b pb-3 pt-2 text-white placeholder:text-white/40 text-base focus:outline-none focus:ring-0 rounded-none transition-colors ${
                  errors.email
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-white/30 focus:border-[#52b788]'
                }`}
              />
              {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
            </div>
          </div>

          {/* Subsequent row: 50/50 split for Telefon and Postnummer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="form-telefon-dark" className="block text-xs font-semibold text-white/80 mb-1">
                Telefonnummer <span className="text-[#52b788]">*</span>
              </label>
              <input
                id="form-telefon-dark"
                type="tel"
                name="telefon"
                required
                autoComplete="tel"
                placeholder="F.eks. 27 13 57 87"
                value={formData.telefon}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b pb-3 pt-2 text-white placeholder:text-white/40 text-base focus:outline-none focus:ring-0 rounded-none transition-colors ${
                  errors.telefon
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-white/30 focus:border-[#52b788]'
                }`}
              />
              {errors.telefon && <p className="mt-1.5 text-xs text-red-400">{errors.telefon}</p>}
            </div>

            <div>
              <label htmlFor="form-postnummer-dark" className="block text-xs font-semibold text-white/80 mb-1">
                Postnummer
              </label>
              <input
                id="form-postnummer-dark"
                type="text"
                name="postnummer"
                autoComplete="postal-code"
                placeholder="F.eks. 6000 Kolding"
                value={formData.postnummer}
                onChange={handleChange}
                className="w-full bg-transparent border-0 border-b border-white/30 focus:border-[#52b788] pb-3 pt-2 text-white placeholder:text-white/40 text-base focus:outline-none focus:ring-0 rounded-none transition-colors"
              />
            </div>
          </div>

          {/* Full width Message textarea */}
          <div>
            <label htmlFor="form-besked-dark" className="block text-xs font-semibold text-white/80 mb-1">
              Beskrivelse af din opgave <span className="text-[#52b788]">*</span>
            </label>
            <textarea
              id="form-besked-dark"
              name="besked"
              rows="3"
              required
              placeholder="Beskriv gerne opgaven, f.eks. træart, højde, placering og adgangsforhold..."
              value={formData.besked}
              onChange={handleChange}
              className={`w-full bg-transparent border-0 border-b pb-3 pt-2 text-white placeholder:text-white/40 text-base focus:outline-none focus:ring-0 rounded-none transition-colors resize-y ${
                errors.besked
                  ? 'border-red-400 focus:border-red-400'
                  : 'border-white/30 focus:border-[#52b788]'
              }`}
            />
            {errors.besked && <p className="mt-1.5 text-xs text-red-400">{errors.besked}</p>}
          </div>

          {/* Solid pill-shaped primary CTA button spanning full width */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 px-8 rounded-full bg-[#52b788] hover:bg-[#40916c] text-[#0b1f15] font-bold text-base tracking-wide transition-all duration-200 shadow-xl flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <span>Sender henvendelse...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
                  <span>Indhent gratis tilbud</span>
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-white/60 text-center pt-1">
            <i className="fa-solid fa-shield-check text-[#52b788] mr-1.5" aria-hidden="true" />
            Dine oplysninger behandles fortroligt og anvendes udelukkende til at besvare dit tilbud.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-[#dce5e0] shadow-sm ${className}`}>
      <div className="mb-6 sm:mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#e4eee8] text-[#1b4332] mb-3">
          <i className="fa-solid fa-clock text-[10px]" aria-hidden="true" />
          <span>Hurtig tilbagemelding inden for 24 timer</span>
        </span>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#10291d] tracking-tight">{title}</h3>
        {subtitle && <p className="text-sm sm:text-base text-[#47574e] mt-2">{subtitle}</p>}
      </div>

      {submitError && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-red-600 mt-0.5" aria-hidden="true" />
          <p>{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
        {/* Honeypot field for antispam - hidden from humans */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Website URL (leave blank)</label>
          <input
            id="website_url"
            name="website_url"
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={formData.website_url}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="form-navn" className="block text-sm font-semibold text-[#1c2620] mb-1.5">
              Dit navn <span className="text-red-600">*</span>
            </label>
            <input
              id="form-navn"
              type="text"
              name="navn"
              required
              autoComplete="name"
              placeholder="F.eks. Anders Jensen"
              value={formData.navn}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border bg-[#faf9f5] text-[#1c2620] text-sm focus:outline-none focus:ring-2 transition-all ${
                errors.navn
                  ? 'border-red-400 focus:ring-red-200 focus:border-red-500'
                  : 'border-[#cfd8d2] focus:ring-[#296046]/20 focus:border-[#296046] focus:bg-white'
              }`}
            />
            {errors.navn && <p className="mt-1 text-xs text-red-600">{errors.navn}</p>}
          </div>

          <div>
            <label htmlFor="form-postnummer" className="block text-sm font-semibold text-[#1c2620] mb-1.5">
              Postnummer
            </label>
            <input
              id="form-postnummer"
              type="text"
              name="postnummer"
              autoComplete="postal-code"
              placeholder="F.eks. 6000 Kolding"
              value={formData.postnummer}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-[#cfd8d2] bg-[#faf9f5] text-[#1c2620] text-sm focus:outline-none focus:ring-2 focus:ring-[#296046]/20 focus:border-[#296046] focus:bg-white transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="form-telefon" className="block text-sm font-semibold text-[#1c2620] mb-1.5">
              Telefonnummer <span className="text-red-600">*</span>
            </label>
            <input
              id="form-telefon"
              type="tel"
              name="telefon"
              required
              autoComplete="tel"
              placeholder="F.eks. 27 13 57 87"
              value={formData.telefon}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border bg-[#faf9f5] text-[#1c2620] text-sm focus:outline-none focus:ring-2 transition-all ${
                errors.telefon
                  ? 'border-red-400 focus:ring-red-200 focus:border-red-500'
                  : 'border-[#cfd8d2] focus:ring-[#296046]/20 focus:border-[#296046] focus:bg-white'
              }`}
            />
            {errors.telefon && <p className="mt-1 text-xs text-red-600">{errors.telefon}</p>}
          </div>

          <div>
            <label htmlFor="form-email" className="block text-sm font-semibold text-[#1c2620] mb-1.5">
              E-mailadresse <span className="text-red-600">*</span>
            </label>
            <input
              id="form-email"
              type="email"
              name="email"
              required
              autoComplete="email"
              placeholder="F.eks. navn@mail.dk"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border bg-[#faf9f5] text-[#1c2620] text-sm focus:outline-none focus:ring-2 transition-all ${
                errors.email
                  ? 'border-red-400 focus:ring-red-200 focus:border-red-500'
                  : 'border-[#cfd8d2] focus:ring-[#296046]/20 focus:border-[#296046] focus:bg-white'
              }`}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="form-besked" className="block text-sm font-semibold text-[#1c2620] mb-1.5">
            Beskrivelse af din opgave <span className="text-red-600">*</span>
          </label>
          <textarea
            id="form-besked"
            name="besked"
            rows="4"
            required
            placeholder="Beskriv gerne opgaven, f.eks. træart, højde, placering og adgangsforhold..."
            value={formData.besked}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-[#faf9f5] text-[#1c2620] text-sm focus:outline-none focus:ring-2 transition-all resize-y ${
              errors.besked
                ? 'border-red-400 focus:ring-red-200 focus:border-red-500'
                : 'border-[#cfd8d2] focus:ring-[#296046]/20 focus:border-[#296046] focus:bg-white'
            }`}
          />
          {errors.besked && <p className="mt-1 text-xs text-red-600">{errors.besked}</p>}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl bg-[#1b4332] hover:bg-[#143627] text-white font-semibold text-base shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Sender henvendelse...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
                <span>Indhent gratis tilbud</span>
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-[#627368] pt-2">
          <i className="fa-solid fa-shield-check text-[#357959] mr-1" aria-hidden="true" />
          Dine oplysninger behandles fortroligt og anvendes udelukkende til at besvare dit tilbud.
        </p>
      </form>
    </div>
  );
}
